/**
 * Lightweight Vyapar i18n — Hindi ↔ English toggle
 * Zero-dependency. Wraps common Vyapar UI labels only (not full portal).
 * Usage:
 *   import { useVyaparI18n, VyaparLangToggle } from './VyaparI18n';
 *   const { t, lang } = useVyaparI18n();
 *   <VyaparLangToggle />
 *   <h1>{t('welcome')}</h1>
 */
import React, { useEffect, useState, useCallback } from 'react';

const DICT = {
  en: {
    welcome: 'Welcome',
    home: 'Home',
    customers: 'Customers',
    billing: 'Billing',
    udhari: 'Udhari',
    accounts: 'Accounts',
    inventory: 'Stock',
    magic_qr: 'Magic QR',
    setup: 'Setup',
    plans: 'Plans',
    add_customer: 'Add Customer',
    add_bill: 'Add Bill',
    add_product: 'Add Product',
    paise_liye: 'Paise Liye',
    paise_diye: 'Paise Diye',
    total_due: 'Total Due',
    total_advance: 'Total Advance',
    my_accounts: 'My Accounts',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    close: 'Close',
    open: 'Open',
    date: 'Date',
    amount: 'Amount',
    name: 'Name',
    mobile: 'Mobile',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    note: 'Note',
    notes: 'Notes',
    category: 'Category',
    subcategory: 'Sub-category',
    manage_categories: 'Manage Categories',
    accounting: 'Accounting',
    chart_of_accounts: 'Chart of Accounts',
    vouchers: 'Vouchers',
    reports: 'Reports',
    trial_balance: 'Trial Balance',
    profit_loss: 'Profit & Loss',
    balance_sheet: 'Balance Sheet',
    day_book: 'Day Book',
    gst_summary: 'GST Summary',
    // Dashboard
    todays_sales: "Today's Sales",
    todays_bills: "Today's Bills",
    total_customers: 'Total Customers',
    pending_dues: 'Pending Dues',
    low_stock: 'Low Stock',
    out_of_stock: 'Out of Stock',
    stock_alerts: 'Stock Alerts',
    quick_actions: 'Quick Actions',
    view_all: 'View All',
    // Billing
    bill_no: 'Bill No.',
    invoice: 'Invoice',
    invoice_no: 'Invoice No.',
    product: 'Product',
    products: 'Products',
    quantity: 'Qty',
    rate: 'Rate',
    price: 'Price',
    stock: 'Stock',
    discount: 'Discount',
    tax: 'Tax',
    gst: 'GST',
    subtotal: 'Subtotal',
    total: 'Total',
    grand_total: 'Grand Total',
    payment: 'Payment',
    paid: 'Paid',
    unpaid: 'Unpaid',
    partial: 'Partial',
    due: 'Due',
    advance: 'Advance',
    // Customer
    search_customer: 'Search customer',
    no_customers_yet: 'No customers yet',
    business_name: 'Business Name',
    industry: 'Industry',
    // Actions
    print: 'Print',
    share: 'Share',
    download: 'Download',
    export: 'Export',
    export_pdf: 'Export PDF',
    export_excel: 'Export Excel',
    whatsapp: 'WhatsApp',
    send_reminder: 'Send Reminder',
    mark_paid: 'Mark as Paid',
    settle: 'Settle',
    view: 'View',
    // Common
    loading: 'Loading…',
    saving: 'Saving…',
    saved: 'Saved',
    success: 'Success',
    failed: 'Failed',
    yes: 'Yes',
    no: 'No',
    confirm: 'Confirm',
    are_you_sure: 'Are you sure?',
    days_left: 'days left',
    expires_in: 'Expires in',
    upgrade: 'Upgrade',
    free_trial: 'Free Trial',
    active: 'Active',
    expired: 'Expired',
    // Plans
    starter: 'Starter',
    pro: 'Pro',
    monthly: 'Monthly',
    yearly: 'Yearly',
    subscribe: 'Subscribe',
    per_month: '/month',
    per_year: '/year',
    lang_label: 'हिं',
    lang_switch_to: 'हिंदी',
  },
  hi: {
    welcome: 'स्वागत है',
    home: 'मुख्य',
    customers: 'ग्राहक',
    billing: 'बिलिंग',
    udhari: 'उधारी',
    accounts: 'खाते',
    inventory: 'स्टॉक',
    magic_qr: 'मैजिक QR',
    setup: 'सेटअप',
    plans: 'प्लान्स',
    add_customer: 'ग्राहक जोड़ें',
    add_bill: 'बिल बनाएँ',
    add_product: 'सामान जोड़ें',
    paise_liye: 'पैसे लिए',
    paise_diye: 'पैसे दिए',
    total_due: 'कुल बकाया',
    total_advance: 'कुल अग्रिम',
    my_accounts: 'मेरे खाते',
    save: 'सहेजें',
    cancel: 'रद्द करें',
    delete: 'हटाएँ',
    edit: 'बदलें',
    add: 'जोड़ें',
    close: 'बंद',
    open: 'खोलें',
    date: 'तारीख',
    amount: 'राशि',
    name: 'नाम',
    mobile: 'मोबाइल',
    phone: 'फ़ोन',
    email: 'ईमेल',
    address: 'पता',
    note: 'नोट',
    notes: 'टिप्पणी',
    category: 'श्रेणी',
    subcategory: 'उप-श्रेणी',
    manage_categories: 'श्रेणियाँ प्रबंधित करें',
    accounting: 'लेखांकन',
    chart_of_accounts: 'खातों की सूची',
    vouchers: 'वाउचर',
    reports: 'रिपोर्ट',
    trial_balance: 'ट्रायल बैलेंस',
    profit_loss: 'लाभ - हानि',
    balance_sheet: 'बैलेंस शीट',
    day_book: 'दिन-पत्रिका',
    gst_summary: 'GST सारांश',
    // Dashboard
    todays_sales: 'आज की बिक्री',
    todays_bills: 'आज के बिल',
    total_customers: 'कुल ग्राहक',
    pending_dues: 'बकाया राशि',
    low_stock: 'कम स्टॉक',
    out_of_stock: 'स्टॉक ख़त्म',
    stock_alerts: 'स्टॉक अलर्ट',
    quick_actions: 'फटाफट काम',
    view_all: 'सब देखें',
    // Billing
    bill_no: 'बिल नं.',
    invoice: 'इनवॉइस',
    invoice_no: 'इनवॉइस नं.',
    product: 'सामान',
    products: 'सामान की सूची',
    quantity: 'मात्रा',
    rate: 'रेट',
    price: 'क़ीमत',
    stock: 'स्टॉक',
    discount: 'छूट',
    tax: 'टैक्स',
    gst: 'GST',
    subtotal: 'उप-योग',
    total: 'कुल',
    grand_total: 'कुल जोड़',
    payment: 'भुगतान',
    paid: 'चुकाया',
    unpaid: 'बाकी',
    partial: 'आंशिक',
    due: 'बकाया',
    advance: 'अग्रिम',
    // Customer
    search_customer: 'ग्राहक खोजें',
    no_customers_yet: 'अभी कोई ग्राहक नहीं',
    business_name: 'दुकान का नाम',
    industry: 'व्यवसाय',
    // Actions
    print: 'प्रिंट',
    share: 'शेयर',
    download: 'डाउनलोड',
    export: 'निर्यात',
    export_pdf: 'PDF निकालें',
    export_excel: 'Excel निकालें',
    whatsapp: 'व्हाट्सएप',
    send_reminder: 'याद दिलाएँ',
    mark_paid: 'चुकाया चिह्नित करें',
    settle: 'सेटल करें',
    view: 'देखें',
    // Common
    loading: 'लोड हो रहा है…',
    saving: 'सहेज रहे हैं…',
    saved: 'सहेजा गया',
    success: 'सफल',
    failed: 'विफल',
    yes: 'हाँ',
    no: 'नहीं',
    confirm: 'पुष्टि करें',
    are_you_sure: 'क्या आप सुनिश्चित हैं?',
    days_left: 'दिन बाकी',
    expires_in: 'समाप्त होगा',
    upgrade: 'अपग्रेड',
    free_trial: 'नि:शुल्क ट्रायल',
    active: 'सक्रिय',
    expired: 'समाप्त',
    // Plans
    starter: 'बेसिक',
    pro: 'प्रो',
    monthly: 'मासिक',
    yearly: 'वार्षिक',
    subscribe: 'सब्सक्राइब',
    per_month: '/महीना',
    per_year: '/साल',
    lang_label: 'EN',
    lang_switch_to: 'English',
  },
};

let listeners = [];
const notify = () => listeners.forEach((fn) => fn());

export const getVyaparLang = () => localStorage.getItem('vyapar_lang') || 'en';
export const setVyaparLang = (l) => { localStorage.setItem('vyapar_lang', l); notify(); };

export const useVyaparI18n = () => {
  const [lang, setLang] = useState(getVyaparLang);
  useEffect(() => {
    const fn = () => setLang(getVyaparLang());
    listeners.push(fn);
    return () => { listeners = listeners.filter((x) => x !== fn); };
  }, []);
  const t = useCallback((key) => (DICT[lang] || DICT.en)[key] || key, [lang]);
  return { t, lang };
};

export const VyaparLangToggle = () => {
  const { t, lang } = useVyaparI18n();
  return (
    <button
      onClick={() => setVyaparLang(lang === 'en' ? 'hi' : 'en')}
      data-testid="vyapar-lang-toggle"
      title={`Switch to ${t('lang_switch_to')}`}
      className="px-2.5 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold shadow-md hover:scale-105 transition"
    >
      🌐 {t('lang_label')}
    </button>
  );
};

export default useVyaparI18n;
